import React, { useEffect, useState, useCallback } from "react";

function ImageModal({ images = [], initialIndex = 0, image = null, onClose }) {
    // Normalize images into array of { src, alt }
    const normalizedImages = React.useMemo(() => {
        if (images && images.length > 0) {
            return images.map((item, idx) => {
                if (typeof item === "string") {
                    return { src: item, alt: `Image ${idx + 1}` };
                }
                return {
                    src: item.src || item,
                    alt: item.alt || `Image ${idx + 1}`
                };
            });
        }
        if (image) {
            const src = typeof image === "string" ? image : image.src;
            const alt = typeof image === "string" ? "Full view" : image.alt || "Full view";
            return [{ src, alt }];
        }
        return [];
    }, [images, image]);

    const [currentIndex, setCurrentIndex] = useState(initialIndex);

    useEffect(() => {
        setCurrentIndex(initialIndex);
    }, [initialIndex, image, images]);

    const hasMultiple = normalizedImages.length > 1;

    const handlePrev = useCallback((e) => {
        if (e) e.stopPropagation();
        setCurrentIndex((prev) => (prev > 0 ? prev - 1 : normalizedImages.length - 1));
    }, [normalizedImages.length]);

    const handleNext = useCallback((e) => {
        if (e) e.stopPropagation();
        setCurrentIndex((prev) => (prev < normalizedImages.length - 1 ? prev + 1 : 0));
    }, [normalizedImages.length]);

    useEffect(() => {
        if (normalizedImages.length === 0) return;

        const handleKeyDown = (e) => {
            if (e.key === "Escape") {
                onClose();
            } else if (e.key === "ArrowLeft" && hasMultiple) {
                handlePrev();
            } else if (e.key === "ArrowRight" && hasMultiple) {
                handleNext();
            }
        };

        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        window.addEventListener("keydown", handleKeyDown);

        return () => {
            document.body.style.overflow = originalOverflow;
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [normalizedImages.length, onClose, hasMultiple, handlePrev, handleNext]);

    if (normalizedImages.length === 0) return null;

    const currentImage = normalizedImages[currentIndex] || normalizedImages[0];

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-md p-4 sm:p-6 md:p-8 cursor-pointer select-none"
            onClick={onClose}
            role="dialog"
            aria-modal="true"
            aria-label="Full screen image preview"
        >
            {/* Top Bar with Counter and Close Button */}
            <div className="absolute top-4 left-4 right-4 sm:top-6 sm:left-6 sm:right-6 flex items-center justify-between z-20 pointer-events-none">
                {hasMultiple ? (
                    <span className="pointer-events-auto text-xs sm:text-sm font-medium text-gray-300 bg-black/60 backdrop-blur-sm px-3 py-1.5 rounded-full border border-white/10">
                        {currentIndex + 1} / {normalizedImages.length}
                    </span>
                ) : <div />}
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        onClose();
                    }}
                    className="pointer-events-auto text-white/80 hover:text-white bg-black/60 hover:bg-black/90 p-2.5 rounded-full border border-white/10 transition focus:outline-none focus:ring-2 focus:ring-white cursor-pointer"
                    aria-label="Close full image view"
                >
                    <svg
                        className="w-5 h-5 sm:w-6 sm:h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                        />
                    </svg>
                </button>
            </div>

            {/* Left Prev Arrow Button */}
            {hasMultiple && (
                <button
                    type="button"
                    onClick={handlePrev}
                    className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 z-20 text-white/80 hover:text-white bg-black/60 hover:bg-black/90 p-2.5 sm:p-3 rounded-full border border-white/10 transition focus:outline-none focus:ring-2 focus:ring-white cursor-pointer shadow-lg hover:scale-105"
                    aria-label="Previous image"
                >
                    <svg
                        className="w-5 h-5 sm:w-6 sm:h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 19l-7-7 7-7"
                        />
                    </svg>
                </button>
            )}

            {/* Main Image View */}
            <div
                className="relative max-w-7xl max-h-[88vh] flex flex-col items-center justify-center cursor-default"
                onClick={(e) => e.stopPropagation()}
            >
                <img
                    key={currentImage.src}
                    src={currentImage.src}
                    alt={currentImage.alt}
                    className="max-h-[80vh] max-w-full object-contain rounded-lg shadow-2xl border border-white/10 transition-opacity duration-200"
                />
                {currentImage.alt && (
                    <p className="mt-3 text-xs sm:text-sm text-center text-gray-300 px-4 line-clamp-2 max-w-2xl">
                        {currentImage.alt}
                    </p>
                )}
            </div>

            {/* Right Next Arrow Button */}
            {hasMultiple && (
                <button
                    type="button"
                    onClick={handleNext}
                    className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 z-20 text-white/80 hover:text-white bg-black/60 hover:bg-black/90 p-2.5 sm:p-3 rounded-full border border-white/10 transition focus:outline-none focus:ring-2 focus:ring-white cursor-pointer shadow-lg hover:scale-105"
                    aria-label="Next image"
                >
                    <svg
                        className="w-5 h-5 sm:w-6 sm:h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                        />
                    </svg>
                </button>
            )}
        </div>
    );
}

export default ImageModal;
